import React, { useState } from 'react';
import { FileIcon, DownloadIcon, TrashIcon } from 'lucide-react';
import { Document, DocumentFolder } from '../types';
import { FileUpload } from './ui/FileUpload';
import { Button } from './ui/Button';
import { Select } from './ui/Select';
export interface OrderDocumentsProps {
  documents: Document[];
  onUpload: (file: File, folder: DocumentFolder) => void;
  onDelete: (documentId: string) => void;
}
export function OrderDocuments({
  documents,
  onUpload,
  onDelete
}: OrderDocumentsProps) {
  const [selectedFolder, setSelectedFolder] = useState<DocumentFolder>('order_documents');
  const [isUploading, setIsUploading] = useState(false);
  const folderOptions = [{
    value: 'borrowers_auth',
    label: 'Borrowers Authorization'
  }, {
    value: 'vendor_review',
    label: 'Vendor Review'
  }, {
    value: 'order_documents',
    label: 'Order Documents'
  }];
  const handleFileSelect = async (file: File) => {
    setIsUploading(true);
    await onUpload(file, selectedFolder);
    setIsUploading(false);
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };
  const getFolderLabel = (folder: DocumentFolder) => {
    const option = folderOptions.find(opt => opt.value === folder);
    return option?.label || folder;
  };
  const groupedDocuments = documents.reduce((acc, doc) => {
    if (!acc[doc.folder]) {
      acc[doc.folder] = [];
    }
    acc[doc.folder].push(doc);
    return acc;
  }, {} as Record<DocumentFolder, Document[]>);
  return <div className="space-y-6" data-id="element-44">
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-45">
        <h3 className="text-sm font-medium text-slate-900 mb-3" data-id="element-46">
          Upload Document
        </h3>
        <div className="space-y-3" data-id="element-47">
          <Select label="Folder" options={folderOptions} value={selectedFolder} onChange={e => setSelectedFolder(e.target.value as DocumentFolder)} data-id="element-48" />
          <FileUpload onFileSelect={handleFileSelect} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" data-id="element-49" />
        </div>
      </div>

      {Object.keys(groupedDocuments).length === 0 ? <div className="text-center py-12 bg-slate-50 rounded-lg border border-slate-200" data-id="element-50">
          <FileIcon className="w-12 h-12 mx-auto text-slate-400 mb-3" data-id="element-51" />
          <p className="text-slate-600" data-id="element-52">No documents uploaded yet</p>
        </div> : <div className="space-y-6" data-id="element-53">
          {(Object.keys(groupedDocuments) as DocumentFolder[]).map(folder => <div key={folder} data-id="element-54">
              <h3 className="text-sm font-medium text-slate-900 mb-3" data-id="element-55">
                {getFolderLabel(folder)}
              </h3>
              <div className="space-y-2" data-id="element-56">
                {groupedDocuments[folder].map(doc => <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg" data-id="element-57">
                    <div className="flex items-center space-x-3 flex-1 min-w-0" data-id="element-58">
                      <FileIcon className="w-8 h-8 text-slate-400 flex-shrink-0" data-id="element-59" />
                      <div className="flex-1 min-w-0" data-id="element-60">
                        <p className="text-sm font-medium text-slate-900 truncate" data-id="element-61">
                          {doc.fileName}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-62">
                          {formatFileSize(doc.fileSize)} • Uploaded by{' '}
                          {doc.uploadedBy} • {formatDate(doc.uploadedAt)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4" data-id="element-63">
                      <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors" aria-label="Download document" data-id="element-64">
                        <DownloadIcon className="w-4 h-4" data-id="element-65" />
                      </button>
                      <button onClick={() => onDelete(doc.id)} className="p-2 text-slate-400 hover:text-red-600 transition-colors" aria-label="Delete document" data-id="element-66">
                        <TrashIcon className="w-4 h-4" data-id="element-67" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>)}
        </div>}
    </div>;
}